// layout.ts — hierarchical (layered) layout for the graph explorer.
//
// Assets are fundamentally hierarchical (parent_asset_uid), so the DEFAULT
// arrangement is a layered top-down tree over the hierarchy edges (parent→child)
// rather than an organic force hairball: children sit *below* their parent, ranks
// stack downward. Typed asset relations and the non-asset kinds (documents,
// interventions, expenses, contacts) are NOT part of the hierarchy — they hang
// laterally off their anchor asset as "satellites", seeded to the side of their
// anchor and separated by a collide-only pass (no charge/link, which would pull
// the layered rows into a radial hairball). Node footprint constants come from
// forceLayout.ts so spacing stays consistent with the collision force.

import { forceSimulation } from 'd3-force'

import { NODE_WIDTH, NODE_HEIGHT, rectCollide, type SimNode } from './forceLayout'
import type { GraphEdge } from './adapter'

// Spacing derived from the card footprint (reused from forceLayout so the
// layered gaps line up with the rectangular-collision force).
const H_GAP = 60
const V_GAP = 90
const COL_STEP = NODE_WIDTH + H_GAP
const ROW_STEP = NODE_HEIGHT + V_GAP

export type Positions = Record<string, { x: number; y: number }>

export interface GraphLayoutInput {
    nodeIds: string[]
    edges: GraphEdge[]
    focusId?: string
    // Previous positions to preserve in incremental mode (includes user pins).
    previous?: Positions
    // Ids the user manually dragged — always kept fixed in incremental mode.
    pinned?: Set<string>
    // 'full' rebuilds the whole layered tree from scratch (ignores previous and
    // pins — used by the Re-layout control and the first render). 'incremental'
    // keeps existing positions and only places nodes that are new, so expanding
    // a node never reshuffles the scene.
    mode: 'full' | 'incremental'
}

// Build parent→children and child→parent maps from the hierarchy edges only.
function hierarchyMaps(edges: GraphEdge[], present: Set<string>) {
    const children = new Map<string, string[]>()
    const parent = new Map<string, string>()
    for (const e of edges) {
        if (e.category !== 'hierarchy') continue
        if (!present.has(e.source) || !present.has(e.target)) continue
        const arr = children.get(e.source) ?? []
        arr.push(e.target)
        children.set(e.source, arr)
        // A node has at most one hierarchy parent (asset forest). First wins.
        if (!parent.has(e.target)) parent.set(e.target, e.source)
    }
    for (const [k, arr] of children) children.set(k, [...arr].sort())
    return { children, parent }
}

// Layered layout of the hierarchy forest: leaf-cursor for x, depth for y, each
// internal node centered over its children. `rootsInOrder` and `childOrder` are
// consumed as-is (no internal sort) so a crossing-reduction pass can control the
// left-to-right ordering of each layer.
function walkForest(rootsInOrder: string[], childOrder: Map<string, string[]>): Positions {
    const pos: Positions = {}
    let cursor = 0
    const visited = new Set<string>()

    const walk = (id: string, depth: number): number => {
        if (visited.has(id)) return pos[id]?.x ?? cursor
        visited.add(id)
        const kids = (childOrder.get(id) ?? []).filter((k) => !visited.has(k))
        const y = depth * ROW_STEP
        if (kids.length === 0) {
            const x = cursor
            cursor += COL_STEP
            pos[id] = { x, y }
            return x
        }
        const xs = kids.map((k) => walk(k, depth + 1))
        const x = (Math.min(...xs) + Math.max(...xs)) / 2
        pos[id] = { x, y }
        return x
    }

    for (const r of rootsInOrder) {
        walk(r, 0)
        cursor += COL_STEP // gap between separate trees
    }
    return pos
}

// Undirected adjacency over every edge (hierarchy + relation) whose endpoints
// are both present. Used to steer barycenter ordering toward related nodes so
// typed-relation edges cross less.
function neighborMap(edges: GraphEdge[], present: Set<string>): Map<string, Set<string>> {
    const m = new Map<string, Set<string>>()
    const add = (a: string, b: string) => {
        const s = m.get(a) ?? new Set<string>()
        s.add(b)
        m.set(a, s)
    }
    for (const e of edges) {
        if (!present.has(e.source) || !present.has(e.target)) continue
        add(e.source, e.target)
        add(e.target, e.source)
    }
    return m
}

// layoutForest lays out the hierarchy forest with a barycenter crossing-reduction
// pass: starting from an alphabetical order, it repeatedly re-orders each parent's
// children (and the root list) by the mean x of their graph neighbours, re-running
// the tidy walk between sweeps. Deterministic (stable tie-break on id, fixed sweep
// count, no Math.random) so URL-replayed graphs are identical.
function layoutForest(
    roots: string[],
    children: Map<string, string[]>,
    edges: GraphEdge[],
    present: Set<string>,
): Positions {
    const order = new Map<string, string[]>()
    for (const [p, kids] of children) order.set(p, [...kids].sort())
    let rootOrder = [...roots].sort()
    let pos = walkForest(rootOrder, order)

    const nbr = neighborMap(edges, present)
    const barycenter = (id: string): number => {
        const ns = nbr.get(id)
        if (!ns || ns.size === 0) return pos[id]?.x ?? 0
        let sum = 0
        let count = 0
        for (const o of ns) {
            if (pos[o]) {
                sum += pos[o].x
                count++
            }
        }
        return count > 0 ? sum / count : pos[id]?.x ?? 0
    }
    const byBarycenter = (ids: string[]): string[] =>
        ids
            .map((id) => ({ id, b: barycenter(id) }))
            .sort((a, z) => a.b - z.b || (a.id < z.id ? -1 : 1))
            .map((s) => s.id)

    for (let sweep = 0; sweep < 4; sweep++) {
        for (const [p, kids] of order) {
            if (kids.length > 1) order.set(p, byBarycenter(kids))
        }
        if (rootOrder.length > 1) rootOrder = byBarycenter(rootOrder)
        pos = walkForest(rootOrder, order)
    }
    return pos
}

// separate runs a collide-ONLY relaxation: fixed nodes hold their seeded x/y,
// free nodes only move enough to stop overlapping. No charge or link force, so a
// layered row never collapses into a radial hairball. Deterministic (explicit
// seeds, no Math.random()).
function separate(seed: Positions, fixed: Set<string>, freeIds: string[], ticks: number): Positions {
    if (freeIds.length === 0) return seed
    const ids = Object.keys(seed)
    const simNodes: SimNode[] = ids.map((id) => {
        const n: SimNode = { id, x: seed[id].x, y: seed[id].y }
        if (fixed.has(id)) {
            n.fx = seed[id].x
            n.fy = seed[id].y
        }
        return n
    })
    const sim = forceSimulation<SimNode>(simNodes)
        .force('collide', rectCollide(NODE_WIDTH, NODE_HEIGHT))
        .stop()
    for (let i = 0; i < ticks; i++) sim.tick()
    const out: Positions = {}
    for (const n of simNodes) out[n.id] = { x: n.x ?? 0, y: n.y ?? 0 }
    return out
}

// Pick a deterministic anchor for a satellite: a placed neighbor (via any edge),
// preferring a hierarchy parent/source.
function anchorFor(id: string, edges: GraphEdge[], placed: Positions): { x: number; y: number } | undefined {
    let fallback: { x: number; y: number } | undefined
    for (const e of edges) {
        let other: string | undefined
        if (e.target === id) other = e.source
        else if (e.source === id) other = e.target
        if (!other || !placed[other]) continue
        if (e.category === 'hierarchy' && e.target === id) return placed[other]
        fallback = placed[other]
    }
    return fallback
}

// Seed satellites in a band strictly BELOW their anchor, horizontally
// centered/spread (wrapping into extra rows for a large fan-out, e.g. a page of
// 25 documents), so expanded sub-elements read as "children of" the node they
// hang off — spec rule 1. Placement is layered: satellites anchored on an
// already-placed node go first, then satellites anchored on those satellites
// (e.g. an intervention's expenses hang under the intervention, which itself
// hangs under its asset). The collide pass then de-overlaps against everything.
const SAT_COLS = 4
function placeGroupBelow(seed: Positions, ax: number, ay: number, ids: string[]) {
    ids.sort()
    const cols = Math.min(ids.length, SAT_COLS)
    ids.forEach((id, i) => {
        const col = i % cols
        const row = Math.floor(i / cols)
        seed[id] = {
            x: ax + (col - (cols - 1) / 2) * COL_STEP,
            y: ay + ROW_STEP + row * ROW_STEP,
        }
    })
}
function seedSatellites(seed: Positions, satellites: string[], edges: GraphEdge[], anchorBase: Positions) {
    const remaining = new Set(satellites)
    // Positions available as anchors this round (grows as satellites are placed).
    const placed: Positions = { ...anchorBase }
    let progress = true
    while (remaining.size > 0 && progress) {
        progress = false
        // Group the satellites whose anchor is already placed, keyed by anchor.
        const groups = new Map<string, { ax: number; ay: number; ids: string[] }>()
        for (const id of remaining) {
            const a = anchorFor(id, edges, placed)
            if (!a) continue
            const key = `${a.x},${a.y}`
            const g = groups.get(key) ?? { ax: a.x, ay: a.y, ids: [] }
            g.ids.push(id)
            groups.set(key, g)
        }
        for (const g of groups.values()) {
            placeGroupBelow(seed, g.ax, g.ay, g.ids)
            for (const id of g.ids) {
                remaining.delete(id)
                placed[id] = seed[id]
                progress = true
            }
        }
    }
    // Anything with no reachable anchor (shouldn't happen) lands at the origin.
    for (const id of remaining) seed[id] = seed[id] ?? { x: 0, y: 0 }
}

// Full layered layout: hierarchy nodes on a top-down grid, satellites relaxed.
function fullLayout(input: GraphLayoutInput): Positions {
    const present = new Set(input.nodeIds)
    const { children, parent } = hierarchyMaps(input.edges, present)

    const hierarchyNodes = new Set<string>()
    for (const [p, kids] of children) {
        hierarchyNodes.add(p)
        for (const k of kids) hierarchyNodes.add(k)
    }
    // A focus with only relations still anchors the scene as a root.
    if (input.focusId && present.has(input.focusId)) hierarchyNodes.add(input.focusId)

    const roots = [...hierarchyNodes].filter((id) => {
        const p = parent.get(id)
        return !p || !hierarchyNodes.has(p)
    })

    const layered = layoutForest(roots, children, input.edges, present)
    for (const id of hierarchyNodes) if (!layered[id]) layered[id] = { x: 0, y: 0 }

    const seed: Positions = { ...layered }
    const satellites = input.nodeIds.filter((id) => !hierarchyNodes.has(id))
    seedSatellites(seed, satellites, input.edges, layered)

    return separate(seed, hierarchyNodes, satellites, 300)
}

// Incremental layout: keep every previously-placed node fixed (including user
// pins), place only new nodes (hierarchy children below their parent, parents
// above their children, everything else as a satellite), then de-overlap.
function incrementalLayout(input: GraphLayoutInput): Positions {
    const previous = input.previous ?? {}
    const present = new Set(input.nodeIds)
    const { children, parent } = hierarchyMaps(input.edges, present)

    const seed: Positions = {}
    for (const id of input.nodeIds) if (previous[id]) seed[id] = previous[id]

    const newIds = input.nodeIds.filter((id) => !previous[id])
    if (newIds.length === 0) return seed

    // New hierarchy children, grouped by their (already placed) parent.
    const childrenByParent = new Map<string, string[]>()
    const newParents: string[] = [] // new nodes that are a parent of a placed node
    const satellites: string[] = []
    for (const id of newIds) {
        const p = parent.get(id)
        if (p && seed[p]) {
            const arr = childrenByParent.get(p) ?? []
            arr.push(id)
            childrenByParent.set(p, arr)
            continue
        }
        const placedKids = (children.get(id) ?? []).filter((k) => seed[k])
        if (placedKids.length > 0) {
            newParents.push(id)
            continue
        }
        satellites.push(id)
    }

    // Place new children in a row below their parent, offset past any siblings
    // that are already on screen.
    for (const [p, kids] of childrenByParent) {
        const existing = (children.get(p) ?? []).filter((k) => seed[k] && !kids.includes(k)).length
        const y = seed[p].y + ROW_STEP
        kids.sort()
        const total = kids.length
        kids.forEach((k, i) => {
            seed[k] = { x: seed[p].x + (existing + i - (total - 1) / 2) * COL_STEP, y }
        })
    }
    // Place new parents above the mean of their placed children.
    for (const id of newParents) {
        const kids = (children.get(id) ?? []).filter((k) => seed[k])
        const xs = kids.map((k) => seed[k].x)
        const ys = kids.map((k) => seed[k].y)
        seed[id] = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: Math.min(...ys) - ROW_STEP }
    }
    // Seed remaining satellites next to an anchor.
    seedSatellites(seed, satellites, input.edges, seed)

    const fixed = new Set(Object.keys(previous).filter((id) => present.has(id)))
    return separate(seed, fixed, newIds, 200)
}

// computeGraphLayout is the single entry point used by the graph route.
export function computeGraphLayout(input: GraphLayoutInput): Positions {
    return input.mode === 'full' ? fullLayout(input) : incrementalLayout(input)
}
